// The sticker catalogue — every die-cut sheet, in one place.
//
// Browser-side twin of HG-CC's `scripts/collateral/lib/sticker-catalogue.mjs`.
// The renderers live in `physical.ts`; this file is the data.
//
// Sizes here are a floor, not a preference. A genie URL is 50 to 60 characters,
// and at the 0.65mm printed-module minimum that puts a hard bottom on how small
// a sticker can carry it. Below roughly 50mm round the code stops decoding, so
// there is no 38mm or 25mm sheet in this book. Shorten the URL first if you
// want one.

import type { StickerOptions } from "./physical";
import type { PackTone } from "./tokens";

export type StickerSection = "round" | "square" | "label" | "glass" | "special";

/** A grid sheet: the geometry, plus the words the book's tables need. */
export interface StickerSheetSpec extends Omit<StickerOptions, "note"> {
  id: string;
  section: StickerSection;
  note: string;
  /** In the activation pack as well as the book. */
  pack?: boolean;
  what: string;
  detail: string;
  size: string;
  stock: string;
  use: string;
}

export const STICKER_SECTIONS: { key: StickerSection; title: string; sub: string; tone: PackTone }[] = [
  {
    key: "round",
    title: "Round stickers.",
    sub: "The default. A circle reads as a badge rather than a label, and it goes on a gate post, a machine, a helmet or a laptop lid without looking like signage.",
    tone: "primary",
  },
  {
    key: "square",
    title: "Squares.",
    sub: "More room for words than a circle of the same width. Doors, cases, bins, folders and anywhere the sticker has to explain itself.",
    tone: "dark",
  },
  {
    key: "label",
    title: "Labels and strips.",
    sub: "Long and thin, for the places a circle will not sit. Shelf edges, machine panels, folder spines, counter lips, door push plates.",
    tone: "secondary",
  },
  {
    key: "glass",
    title: "Glass and big surfaces.",
    sub: "Window clings and large discs. Print on static cling film so they come off cleanly, or white vinyl if they are going on for good.",
    tone: "primary",
  },
  {
    key: "special",
    title: "Mixed and magnets.",
    sub: "One starter sheet with a bit of everything, for the first walk around the building, plus magnet artwork for anything steel.",
    tone: "dark",
  },
];

export const STICKER_CATALOGUE: StickerSheetSpec[] = [
  // ── Round ──────────────────────────────────────────────────────────────────
  {
    id: "round-60", section: "round", pack: true,
    shape: "round", sizeMm: 60, cols: 3, rows: 4, label: "stickers-round-60mm",
    what: "Round stickers, 60mm", detail: "12 up, the everyday one",
    size: "60mm circle, 12 up", stock: "White vinyl, matte laminate",
    use: "Gate posts, doors, laptop lids, toolboxes, equipment",
    note: "Round stickers, 60mm, 12 up. Cut on the dashed circle. Vinyl with a matte laminate for outdoor and vehicle use.",
  },
  {
    id: "round-60-code", section: "round",
    shape: "round", sizeMm: 60, cols: 3, rows: 4, variant: "qr", label: "stickers-round-60mm-code",
    what: "Round stickers, 60mm, code led", detail: "12 up, name only under the code",
    size: "60mm circle, 12 up", stock: "White vinyl, matte laminate",
    use: "Where the surrounding sign already says what it is",
    note: "Round stickers, 60mm, 12 up, code led. Same size as the sheet before it with the prompt line dropped, so the code runs larger. Use where a poster nearby already explains it.",
  },
  {
    id: "round-50", section: "round", pack: true,
    shape: "round", sizeMm: 50, cols: 3, rows: 5, variant: "qr", label: "stickers-round-50mm",
    what: "Round stickers, 50mm", detail: "15 up, small surfaces",
    size: "50mm circle, 15 up", stock: "White vinyl, matte laminate",
    use: "Drink bottles, phone cases, keyrings, folders, small gear",
    note: "Round stickers, 50mm, 15 up. Code led, for small surfaces: drink bottles, tool cases, phone cases, keyrings. This is the smallest circle a genie URL still scans from.",
  },
  {
    id: "round-75", section: "round",
    shape: "round", sizeMm: 75, cols: 2, rows: 3, label: "stickers-round-75mm",
    what: "Round stickers, 75mm", detail: "6 up, reads from further back",
    size: "75mm circle, 6 up", stock: "White vinyl, matte laminate",
    use: "Vehicle panels, bins, gates, big equipment, workshop doors",
    note: "Round stickers, 75mm, 6 up. The big circle. Scans from arm's length and further, so it suits a vehicle panel or a gate somebody walks up to.",
  },

  // ── Square ─────────────────────────────────────────────────────────────────
  {
    id: "square-75", section: "square", pack: true,
    shape: "square", sizeMm: 75, cols: 2, rows: 3, label: "stickers-square-75mm",
    what: "Square stickers, 75mm", detail: "6 up, carries a line of copy",
    size: "75mm square, 6 up", stock: "White vinyl, matte laminate",
    use: "Doors, bins, equipment cases, noticeboards",
    note: "Square stickers, 75mm with rounded corners, 6 up. Cut on the dashed guide. Doors, bins, equipment cases, noticeboards.",
  },
  {
    id: "square-95", section: "square",
    shape: "square", sizeMm: 95, cols: 2, rows: 2, label: "stickers-square-95mm",
    what: "Square stickers, 95mm", detail: "4 up, the large square",
    size: "95mm square, 4 up", stock: "White vinyl, matte laminate",
    use: "Entry doors, service hatches, plant and machinery",
    note: "Square stickers, 95mm, 4 up. The large square. Big enough to read the prompt line from a couple of metres, so it works as a small sign in its own right.",
  },
  {
    id: "square-50-code", section: "square",
    shape: "square", sizeMm: 50, cols: 3, rows: 4, variant: "qr", label: "stickers-square-50mm-code",
    what: "Square stickers, 50mm, code led", detail: "12 up, code and name",
    size: "50mm square, 12 up", stock: "White vinyl or paper label",
    use: "Folders, files, devices, shelving, anywhere small and flat",
    note: "Square stickers, 50mm, 12 up, code led. A square fits more code into the same width than a circle does, so this is the smallest sticker in the book.",
  },

  // ── Labels ─────────────────────────────────────────────────────────────────
  {
    id: "label-90x35", section: "label", pack: true,
    shape: "rect", wMm: 90, hMm: 35, cols: 2, rows: 7, label: "stickers-label-90x35mm",
    what: "Label stickers, 90 x 35mm", detail: "14 up, shelf edges",
    size: "90 x 35mm, 14 up", stock: "White vinyl or paper label",
    use: "Shelf edges, folders, machine panels, counter lips",
    note: "Label stickers, 90 x 35mm, 14 up. Cut on the dashed guide. Shelf edges, folders, machine panels, anywhere a circle will not sit.",
  },
  {
    id: "label-140x45", section: "label",
    shape: "rect", wMm: 140, hMm: 45, cols: 1, rows: 5, label: "stickers-label-140x45mm",
    what: "Label stickers, 140 x 45mm", detail: "5 up, the wide one",
    size: "140 x 45mm, 5 up", stock: "White vinyl, matte laminate",
    use: "Door push plates, vehicle doors, counter fronts, tool chests",
    note: "Label stickers, 140 x 45mm, 5 up. The wide one. Sits along a door push plate, a counter front or a vehicle door where a tall sticker would not.",
  },

  // ── Specials ───────────────────────────────────────────────────────────────
  {
    id: "magnets-100x70", section: "special",
    shape: "rect", wMm: 100, hMm: 70, cols: 2, rows: 3, variant: "qr", label: "magnets-100x70mm",
    what: "Magnets, 100 x 70mm", detail: "6 up, anything steel",
    size: "100 x 70mm, 6 up", stock: "0.6mm magnetic sheet, matte",
    use: "Fridges, vans, whiteboards, switchboards, roller doors",
    note: "Magnet artwork, 100 x 70mm, 6 up. Print on 0.6mm magnetic sheet with a matte finish. Goes on a fridge, a van door, a whiteboard or a switchboard and comes off without a mark.",
  },
];
